/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect, useState } from "react";

interface RejectMonsterModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Submit the rejection reason; resolves true on success. The monster is returned to 'Private'.
  onSubmit: (reason: string) => Promise<boolean>;
}

// Shown when an approver rejects another user's monster. Collects the reason (a future
// owner-facing message) before submitting.
const RejectMonsterModal: React.FC<RejectMonsterModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [reason, setReason] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // The modal stays mounted (returns null) when closed; reset each time it reopens.
  useEffect(() => {
    setReason('');
    setSubmitting(false);
    setError('');
  }, [isOpen]);

  if (!isOpen) return null;

  const canSubmit = reason.trim() !== '' && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    setSubmitting(true);
    setError('');
    const ok = await onSubmit(reason.trim());
    setSubmitting(false);
    if (!ok) {
      setError('Sorry, the rejection could not be submitted. Please try again.');
    }
    // On success the parent closes the modal and navigates away.
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ width: '500px', height: 'auto', minHeight: '250px' }} onClick={(e) => e.stopPropagation()}>
        <span className="modal-title" style={{ display: 'block', textAlign: 'center' }}>Reject Monster</span>
        <div>
          <p>Explain why this monster is being rejected. The owner will be returned an editable copy
            (set to Private), and this reason will be shared with them.</p>
          <label style={{ display: 'block', fontWeight: 'bold' }}>Rejection reason:
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4}
              style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '4px', padding: '6px', resize: 'vertical' }} />
          </label>
          {error && <p style={{ color: 'darkred', fontWeight: 'bold' }}>{error}</p>}
        </div>
        <div className="modal-actions" style={{ textAlign: 'center' }}>
          <button type="button" onClick={handleSubmit} disabled={!canSubmit}>{submitting ? 'Rejecting…' : 'Reject'}</button>
          <button type="button" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default RejectMonsterModal;
